/**
 * Approvals screen (mockup `docs/mockups/phase-2/approvals.html`): the queue of pending requests,
 * the decision buttons and the table of decided ones.
 *
 * Keys are literal segments only: the i18n guard cannot see a key built inside a template literal,
 * so enum values (`step`, `external_action`, `approved`, `panel`) are mapped to literal keys in the
 * page instead of interpolated (see `scripts/guards/i18n-keys.mjs`).
 */
export const approvals = {
  'approvals.title': 'Затвердження',
  'approvals.intro': 'Черга рішень: результат прогону, незворотна дія або перевищення порогу вартості. Рішення однакове в панелі й у Telegram.',

  'approvals.kpi.pending': 'Очікують',
  'approvals.kpi.oldest': 'Найстаріше',
  'approvals.kpi.decided7': 'Вирішено за 7 днів',
  'approvals.kpi.none': 'немає',

  'approvals.pending': 'Очікують',
  'approvals.decided': 'Вирішені',
  'approvals.empty.pending': 'Немає запитів, що очікують рішення.',
  'approvals.empty.decided': 'Ще немає вирішених запитів.',

  // ---- kinds and risk ----
  'approvals.kind.step': 'Результат прогону',
  'approvals.kind.externalAction': 'Незворотна дія',
  'approvals.kind.cost': 'Поріг вартості',
  'approvals.kind.retry': 'Повтор виклику',
  'approvals.kind.plan': 'План',
  'approvals.kind.newAgent': 'Новий агент',
  'approvals.kind.knowledge': 'Знання',
  'approvals.risk.low': 'низький ризик',
  'approvals.risk.medium': 'середній ризик',
  'approvals.risk.high': 'високий ризик',

  // ---- card body ----
  'approvals.desc.step': 'Чернетка, надіслана на затвердження після завершення прогону.',
  'approvals.desc.tool': 'Виклик інструмента {tool} потребує підтвердження перед виконанням.',
  'approvals.desc.retry': 'Виклик інструмента {tool} обірвався на пів дорозі (спроба {attempt}): підтвердь повтор або відхили.',
  'approvals.desc.cost': 'Оцінка наступного виклику моделі перевищує поріг затвердження екземпляра.',
  'approvals.estimate': 'Оцінка',
  'approvals.threshold': 'Поріг',
  'approvals.noThreshold': 'не задано',
  'approvals.cost': 'Вартість прогону',
  'approvals.textTruncated': 'Показано початок чернетки.',
  'approvals.expires': 'Діє до {when}',

  'approvals.comment': 'Коментар (обов’язковий для доопрацювання)',
  'approvals.comment.placeholder': 'Що виправити…',
  'approvals.approve': 'Погодити',
  'approvals.rework': 'На доопрацювання',
  'approvals.reject': 'Відхилити',
  'approvals.reworkStarted': 'Рішення записано, створено новий прогін на доопрацювання.',

  // ---- age ----
  'approvals.age.now': 'щойно',
  'approvals.age.minutes': '{n} хв тому',
  'approvals.age.hours': '{n} год тому',
  'approvals.age.days': '{n} дн тому',
  // the KPI strip shows a bare duration, the card a "… тому"
  'approvals.dur.minutes': '{n} хв',
  'approvals.dur.hours': '{n} год',
  'approvals.dur.days': '{n} дн',

  // ---- filters ----
  'approvals.filter.allBusinesses': 'Усі бізнеси',
  'approvals.filter.allKinds': 'Усі',

  // ---- decided table ----
  'approvals.col.when': 'Коли',
  'approvals.col.kind': 'Вид',
  'approvals.col.agent': 'Агент',
  'approvals.col.task': 'Задача',
  'approvals.col.decision': 'Рішення',
  'approvals.col.channel': 'Канал',
  'approvals.col.who': 'Хто',
  'approvals.decision.approved': 'погоджено',
  'approvals.decision.rework': 'на доопрацювання',
  'approvals.decision.rejected': 'відхилено',
  'approvals.decision.expired': 'протерміновано',
  'approvals.channel.panel': 'панель',
  'approvals.channel.telegram': 'Telegram',
  'approvals.channel.mcp': 'помічник',
  'approvals.shown': 'Показано {shown} з {total}, сторінка {page}',
  'approvals.prev': 'Назад',
  'approvals.next': 'Далі',

  // ---- errors ----
  'approvals.error.alreadyDecided': 'Цей запит уже вирішено — в панелі або в Telegram.',
  'approvals.error.expired': 'Термін цього запиту минув: прогін уже зупинено, рішення не приймається.',
  'approvals.error.reasonRequired': 'Для доопрацювання потрібен коментар: що саме виправити.',
  'approvals.error.notApplicable': 'Таке рішення не застосовне до цього запиту.',
  'approvals.error.budgetHardLimit': 'Бюджет вичерпано: новий прогін на доопрацювання не запуститься.',
  'approvals.error.instanceNotActive': 'Екземпляр агента не активний, прогін не запустити.',
  'approvals.error.invalidStatus': 'Прогін уже в іншому стані — онови список.',
  'approvals.error.notFound': 'Запит не знайдено — можливо, його вже вирішили.',
  'approvals.reload': 'Оновити',

  // ---- sidebar badge ----
  'approvals.badge': 'Очікують рішення: {n}',
} as const;
